/**
 * dink-api's money refusals for a STAFF session (DNKAPI-0670, DNKADM-0375) as plain copy.
 * Without this they read as the generic "not authorized" line (or nothing, for the 423 and
 * 503), which tells the desk nothing about what to do next:
 *  - `STAFF_NOT_ALLOWED` (403) — staff cannot do this, PIN or not (Adjust credits);
 *  - `MANAGER_PIN_REQUIRED` (403) — the action needs the manager PIN and none came with it;
 *  - `MANAGER_PIN_NOT_SET` (403) — the location has no manager PIN, so staff cannot do it;
 *  - `MANAGER_PIN_INVALID` (403) — the PIN sent is not the location's;
 *  - `MANAGER_PIN_LOCKED` (423) — too many wrong PINs (the comp lockout, shared with comps);
 *  - `MANAGER_PIN_UNVERIFIABLE` (503) — the PIN cannot be checked right now; nothing changed.
 * The error mappers check this FIRST and fall back to their own copy for anything else. The
 * comp flow keeps its own `COMP_PIN_*` codes (`features/members/compPin.ts`).
 */
import { v2ErrorCode } from '@/api/v2Errors';

export const MANAGER_PIN_REQUIRED_CODE = 'MANAGER_PIN_REQUIRED';

const MONEY_REFUSAL_COPY: Readonly<Record<string, string>> = {
  STAFF_NOT_ALLOWED: 'Only an owner or admin can do this.',
  [MANAGER_PIN_REQUIRED_CODE]: 'This needs the manager PIN. Ask a manager to enter it.',
  MANAGER_PIN_NOT_SET:
    'This location has no manager PIN set, so staff can’t do this. Ask an owner or admin.',
  MANAGER_PIN_INVALID: 'That PIN isn’t right.',
  MANAGER_PIN_LOCKED: 'Too many wrong PINs. Try again in 15 minutes.',
  MANAGER_PIN_UNVERIFIABLE:
    'The manager PIN can’t be checked right now, so nothing was changed. Try again shortly.',
};

/** The codes that are about the PIN the desk typed, so the copy belongs under the PIN field. */
const PIN_FIELD_CODES: ReadonlySet<string> = new Set([
  'MANAGER_PIN_INVALID',
  'MANAGER_PIN_LOCKED',
  'MANAGER_PIN_UNVERIFIABLE',
]);

/** The copy for a DNKAPI-0670 money refusal, or null when the error is something else. */
export function moneyRefusalMessage(error: unknown): string | null {
  const code = v2ErrorCode(error);
  return code !== undefined ? (MONEY_REFUSAL_COPY[code] ?? null) : null;
}

/** The copy to show under the PIN field (a wrong, locked or uncheckable PIN), else null. */
export function managerPinFieldMessage(error: unknown): string | null {
  const code = v2ErrorCode(error);
  return code !== undefined && PIN_FIELD_CODES.has(code) ? (MONEY_REFUSAL_COPY[code] ?? null) : null;
}

/** True when the server asked for the manager PIN — the dialog then shows the PIN field. */
export function isManagerPinRequired(error: unknown): boolean {
  return v2ErrorCode(error) === MANAGER_PIN_REQUIRED_CODE;
}
